<template>
  <div class="pb-4">
    <v-btn small class="ma-3" @click="$router.back()" color="info" rounded outlined>
      <v-icon left>mdi-arrow-left-bold</v-icon> Volver
    </v-btn>

    <v-container fluid class="pt-0">
      <!-- HEADER -->
      <v-card class="mb-4 blue darken-3" dark elevation="3">
        <v-card-text class="py-4 px-5">
          <div class="d-flex align-start flex-wrap" style="gap: 16px">
            <v-avatar color="blue darken-4" size="54" class="flex-shrink-0 mt-1">
              <v-icon size="30">mdi-calendar-clock</v-icon>
            </v-avatar>

            <div class="flex-grow-1" style="min-width: 0">
              <div class="overline blue--text text--lighten-3 mb-n1">Configuración de Clases</div>
              <div class="text-h6 font-weight-bold white--text" style="line-height: 1.4">
                {{ programa ? programa.nombre_compuesto : 'Cargando...' }}
              </div>
              <div class="d-flex flex-wrap mt-2" style="gap: 6px" v-if="programaInfo">
                <v-chip x-small color="blue darken-4" dark label>
                  <v-icon left x-small>mdi-barcode</v-icon>
                  {{ programaInfo.codigo || 'Sin código' }}
                </v-chip>
                <v-chip x-small color="blue darken-4" dark>
                  <v-icon left x-small>{{ modalidadIcon(programaInfo.modalidad) }}</v-icon>
                  {{ modalidadLabel(programaInfo.modalidad) }}
                </v-chip>
                <v-chip x-small color="blue darken-4" dark v-if="programaInfo.version">
                  V{{ programaInfo.version }} · G{{ programaInfo.grupo || '—' }}
                </v-chip>
                <v-chip x-small color="blue darken-4" dark v-if="programaInfo.gestion">
                  <v-icon left x-small>mdi-calendar-range</v-icon>
                  {{ programaInfo.gestion }}
                </v-chip>
              </div>
            </div>

            <div class="d-flex flex-column align-end flex-shrink-0" style="gap: 8px">
              <v-chip v-if="programaInfo && programaInfo.estado_programa" x-small :color="colorEstadoDestino(programaInfo.estado_programa.codigo)" dark label class="font-weight-bold">
                <v-icon left x-small>mdi-flag-outline</v-icon>
                {{ programaInfo.estado_programa.nombre }}
              </v-chip>
              <v-chip v-else-if="programaInfo" x-small :color="estadoColor(programaInfo.estado)" dark>
                {{ estadoLabel(programaInfo.estado) }}
              </v-chip>
              <v-chip v-if="programaInfo && programaInfo.etapa" x-small color="blue-grey darken-1" dark label>
                <v-icon left x-small>mdi-layers-outline</v-icon>
                {{ programaInfo.etapa.nombre }}
              </v-chip>
              <v-chip v-if="programaInfo && programaInfo.es_estrella" x-small color="amber darken-2" dark label class="font-weight-bold">
                <v-icon left x-small>mdi-star</v-icon> Estrella
              </v-chip>
              <v-chip v-if="programaInfo && programaInfo.inicio_excepcional_modulo0" x-small color="deep-purple" dark label class="font-weight-bold">
                <v-icon left x-small>mdi-rocket-launch-outline</v-icon> Inicio Excepcional
              </v-chip>
              <v-chip v-if="programa" color="white" class="blue--text text--darken-4" small>
                <v-icon left x-small>mdi-view-list</v-icon>
                {{ modulos.length }} módulo(s)
              </v-chip>
              <v-chip
                v-if="programaInfo"
                small
                :color="configuracionEstado === 'completo' ? 'green darken-1' : configuracionEstado === 'parcial' ? 'orange darken-2' : 'red darken-2'"
                dark
              >
                <v-icon left x-small>
                  {{ configuracionEstado === 'completo' ? 'mdi-check-circle' : configuracionEstado === 'parcial' ? 'mdi-alert-circle' : 'mdi-close-circle' }}
                </v-icon>
                {{ configuracionEstado === 'completo' ? 'Configurado' : configuracionEstado === 'parcial' ? 'Incompleto' : 'Sin configurar' }}
              </v-chip>
              <v-btn
                v-if="estado_programa_codigo === 'ARRASTRE_CRITICO' && $store.state.CARGO && $store.state.CARGO.id === 1"
                small color="red darken-2" dark class="mt-1"
                @click="dialogDecision = true"
              >
                <v-icon left small>mdi-gavel</v-icon> Decidir
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- INFORMACIÓN DEL PROGRAMA (solo lectura) -->
      <v-card v-if="programaInfo" outlined class="mb-4 rounded-lg">
        <v-card-title class="subtitle-1 font-weight-bold py-3 px-4 blue-grey darken-3 white--text">
          <v-icon left color="white">mdi-information-outline</v-icon>
          Información del Programa
          <v-spacer />
          <v-chip x-small color="blue-grey lighten-3" text-color="blue-grey darken-4" label class="font-weight-bold">
            Solo lectura
          </v-chip>
        </v-card-title>

        <v-card-text class="pa-3">
          <!-- Fila 1: Identificadores -->
          <v-row dense class="mb-1">
            <v-col cols="12" sm="6" md="4">
              <div class="info-box pa-2 rounded" style="background:#f5f5f5; border-left:3px solid #546e7a">
                <div class="d-flex align-center mb-1">
                  <v-icon x-small color="blue-grey darken-1" class="mr-1">mdi-book-education-outline</v-icon>
                  <span class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Programa</span>
                </div>
                <div class="body-2 font-weight-bold" style="line-height:1.35">{{ programaInfo.nombre_compuesto }}</div>
                <div class="caption grey--text" v-if="programaInfo.nombre_corto">{{ programaInfo.nombre_corto }}</div>
              </div>
            </v-col>

            <v-col cols="6" sm="3" md="2">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#e3f2fd; border-left:3px solid #1565c0; height:100%">
                <v-icon small color="blue darken-3" class="mr-2 flex-shrink-0">mdi-barcode</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Código</div>
                  <div class="body-2 font-weight-bold blue--text text--darken-3">{{ programaInfo.codigo || '—' }}</div>
                </div>
              </div>
            </v-col>

            <v-col cols="6" sm="3" md="2">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#e8eaf6; border-left:3px solid #3949ab; height:100%">
                <v-icon small :color="'indigo darken-2'" class="mr-2 flex-shrink-0">{{ modalidadIcon(programaInfo.modalidad) }}</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Modalidad</div>
                  <div class="body-2 font-weight-medium">{{ modalidadLabel(programaInfo.modalidad) }}</div>
                </div>
              </div>
            </v-col>

            <v-col cols="4" sm="2" md="1">
              <div class="info-box pa-2 rounded text-center" style="background:#f5f5f5; border-left:3px solid #546e7a; height:100%">
                <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Ver.</div>
                <div class="body-2 font-weight-bold">{{ programaInfo.version || '—' }}</div>
              </div>
            </v-col>

            <v-col cols="4" sm="2" md="1">
              <div class="info-box pa-2 rounded text-center" style="background:#f5f5f5; border-left:3px solid #546e7a; height:100%">
                <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Grupo</div>
                <div class="body-2 font-weight-bold">{{ programaInfo.grupo || '—' }}</div>
              </div>
            </v-col>

            <v-col cols="4" sm="2" md="1">
              <div class="info-box pa-2 rounded text-center" style="background:#f5f5f5; border-left:3px solid #546e7a; height:100%">
                <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Gestión</div>
                <div class="body-2 font-weight-bold">{{ programaInfo.gestion || '—' }}</div>
              </div>
            </v-col>

            <v-col cols="4" sm="2" md="1">
              <div class="info-box pa-2 rounded text-center" style="background:#f3e5f5; border-left:3px solid #8e24aa; height:100%">
                <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Fase</div>
                <div class="body-2 font-weight-bold">{{ programaInfo.fase != null ? programaInfo.fase : '—' }}</div>
              </div>
            </v-col>

            <v-col cols="6" sm="3" md="2">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#f3e5f5; border-left:3px solid #8e24aa; height:100%">
                <v-icon small color="purple darken-2" class="mr-2 flex-shrink-0">mdi-book-open-variant</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Tipo Programa</div>
                  <div class="body-2 font-weight-medium text-capitalize">{{ programaInfo.tipo_programa || '—' }}</div>
                </div>
              </div>
            </v-col>

            <v-col cols="4" sm="2" md="1">
              <div class="info-box pa-2 rounded text-center" style="background:#fce4ec; border-left:3px solid #e91e63; height:100%">
                <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Nota Aprob.</div>
                <div class="body-2 font-weight-bold">{{ programaInfo.nota_aprobacion != null ? programaInfo.nota_aprobacion : '—' }}</div>
              </div>
            </v-col>
          </v-row>

          <!-- Fila 2: Estado y fechas -->
          <v-row dense class="mb-1">
            <v-col cols="6" sm="3" md="2">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#fafafa; border-left:3px solid #78909c; height:100%">
                <v-icon small color="blue-grey" class="mr-2 flex-shrink-0">mdi-flag-outline</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Estado Prog.</div>
                  <v-chip x-small :color="colorEstadoDestino(programaInfo.estado_programa && programaInfo.estado_programa.codigo)" dark class="mt-1">
                    {{ programaInfo.estado_programa ? (programaInfo.estado_programa.nombre || programaInfo.estado_programa.codigo) : (estadoLabel(programaInfo.estado)) }}
                  </v-chip>
                </div>
              </div>
            </v-col>

            <v-col cols="6" sm="3" md="2">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#fafafa; border-left:3px solid #78909c; height:100%">
                <v-icon small color="blue-grey darken-1" class="mr-2 flex-shrink-0">mdi-layers-outline</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Etapa</div>
                  <div class="body-2 font-weight-medium" style="font-size:11px">{{ programaInfo.etapa ? programaInfo.etapa.nombre : '—' }}</div>
                </div>
              </div>
            </v-col>

            <v-col cols="6" sm="3" md="1">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#fafafa; border-left:3px solid #78909c; height:100%">
                <v-icon small :color="programaInfo.is_campania ? 'green darken-2' : 'grey darken-1'" class="mr-2 flex-shrink-0">mdi-bullhorn-outline</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Campaña</div>
                  <v-chip x-small :color="programaInfo.is_campania ? 'green darken-2' : 'grey'" dark class="mt-1">{{ programaInfo.is_campania ? 'Sí' : 'No' }}</v-chip>
                </div>
              </div>
            </v-col>

            <v-col cols="auto" class="d-flex align-center" style="gap:8px">
              <v-chip v-if="programaInfo.es_estrella" small color="amber darken-2" dark label class="font-weight-bold">
                <v-icon left x-small>mdi-star</v-icon> Estrella
              </v-chip>
              <v-chip v-if="programaInfo.inicio_excepcional_modulo0" small color="deep-purple" dark label class="font-weight-bold">
                <v-icon left x-small>mdi-rocket-launch-outline</v-icon> Inicio Excepcional
              </v-chip>
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#e8f5e9; border-left:3px solid #43a047; height:100%">
                <v-icon small color="green darken-2" class="mr-2 flex-shrink-0">mdi-calendar-account</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Fecha Matrícula</div>
                  <div class="body-2 font-weight-medium">{{ programaInfo.fecha_matricula || '—' }}</div>
                </div>
              </div>
            </v-col>

            <v-col cols="6" sm="4" md="3">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#e3f2fd; border-left:3px solid #1e88e5; height:100%">
                <v-icon small color="blue darken-2" class="mr-2 flex-shrink-0">mdi-calendar-start</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Fecha Inicio</div>
                  <div class="body-2 font-weight-medium">{{ programaInfo.fecha_inicio || '—' }}</div>
                </div>
              </div>
            </v-col>

            <v-col cols="6" sm="4" md="3">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#e3f2fd; border-left:3px solid #1e88e5; height:100%">
                <v-icon small color="blue darken-2" class="mr-2 flex-shrink-0">mdi-calendar-end</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Fecha Fin</div>
                  <div class="body-2 font-weight-medium">{{ programaInfo.fecha_fin || '—' }}</div>
                </div>
              </div>
            </v-col>

            <v-col v-if="programaInfo.estado_programa_desde" cols="6" sm="4" md="3">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#fff8e1; border-left:3px solid #f9a825; height:100%">
                <v-icon small color="orange darken-2" class="mr-2 flex-shrink-0">mdi-clock-start</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Estado desde</div>
                  <div class="body-2 font-weight-medium">{{ (programaInfo.estado_programa_desde || '').slice(0, 10) }}</div>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Fila 3: Contactos (solo si existen) -->
          <v-row dense v-if="programaInfo.sede_id || programaInfo.coordinador || programaInfo.responsable" class="mb-1">
            <v-col cols="6" sm="3" md="2" v-if="programaInfo.sede_id">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#fafafa; border-left:3px solid #78909c">
                <v-icon small color="blue-grey" class="mr-2 flex-shrink-0">mdi-map-marker-outline</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Sede</div>
                  <div class="body-2">{{ programaInfo.sede_id }}</div>
                </div>
              </div>
            </v-col>
            <v-col cols="6" sm="3" md="2" v-if="programaInfo.coordinador">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#fafafa; border-left:3px solid #78909c">
                <v-icon small color="blue-grey" class="mr-2 flex-shrink-0">mdi-account-tie</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Coordinador</div>
                  <div class="body-2">{{ programaInfo.coordinador.coordinador_id }}</div>
                </div>
              </div>
            </v-col>
            <v-col cols="6" sm="3" md="2" v-if="programaInfo.responsable">
              <div class="info-box pa-2 rounded d-flex align-center" style="background:#fafafa; border-left:3px solid #78909c">
                <v-icon small color="blue-grey" class="mr-2 flex-shrink-0">mdi-account-star-outline</v-icon>
                <div>
                  <div class="overline" style="font-size:9px;letter-spacing:.8px;line-height:1">Responsable</div>
                  <div class="body-2">{{ programaInfo.responsable.responsable_id }}</div>
                </div>
              </div>
            </v-col>
          </v-row>

            <!-- Módulos con fechas reales -->
            <v-row v-if="programaModulosOrdenados.length > 0" dense class="mb-1">
              <v-col cols="12"><v-divider class="my-2" /></v-col>
              <v-col cols="12">
                <div class="caption font-weight-medium mb-2" style="color: #546e7a; letter-spacing: .5px">
                  <v-icon x-small color="blue-grey" class="mr-1">mdi-view-list-outline</v-icon>
                  MÓDULOS Y FECHAS REALES
                </div>
                <div style="overflow-x:auto">
                <v-simple-table dense>
                  <thead style="background: #37474f">
                    <tr>
                      <th class="white--text text-center" style="width: 50px">N°</th>
                      <th class="white--text">Nombre del Módulo</th>
                      <th class="white--text" style="width: 110px">Estado</th>
                      <th class="white--text" style="width: 100px">Calificación</th>
                      <th class="white--text" style="width: 120px">Fecha Inicio</th>
                      <th class="white--text" style="width: 120px">Fecha Fin</th>
                      <th class="white--text" style="width: 120px">1ra Instancia</th>
                      <th class="white--text" style="width: 120px">2da Instancia</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="pm in programaModulosOrdenados" :key="pm.programa_modulo_id">
                      <td class="text-center">
                        <v-chip x-small color="blue-grey darken-2" dark>{{ pm.numero }}</v-chip>
                      </td>
                      <td class="body-2">{{ pm.nombre || ('Módulo ' + pm.numero) }}</td>
                      <td>
                        <v-chip v-if="pm.estado" x-small
                          :color="pm.estado === 'CULMINADO' ? 'green darken-1' : pm.estado === 'EN_PROCESO' ? 'blue darken-1' : pm.estado === 'PENDIENTE' ? 'orange darken-1' : 'grey'"
                          dark>{{ pm.estado }}</v-chip>
                        <span v-else class="caption grey--text">—</span>
                      </td>
                      <td class="caption">{{ pm.tipo_calificacion || '—' }}</td>
                      <td class="body-2">{{ pm.fecha_inicio || '—' }}</td>
                      <td class="body-2">{{ pm.fecha_fin || '—' }}</td>
                      <td class="body-2">{{ pm.fecha_primera_instancia || '—' }}</td>
                      <td class="body-2">{{ pm.fecha_segunda_instancia || '—' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
                </div>
              </v-col>
            </v-row>
        </v-card-text>
      </v-card>

      <!-- FECHAS PROYECTADAS -->
      <v-card outlined class="mb-4 rounded-lg">
        <v-card-title class="subtitle-1 font-weight-bold py-3 px-4 teal darken-2 white--text">
          <v-icon left color="white">mdi-calendar-clock</v-icon>
          Fechas Proyectadas
          <v-spacer />
          <v-progress-circular v-if="loadingFechas" indeterminate color="white" size="20" width="2" />
          <v-chip v-else-if="sortedFechasProyectadas.length" x-small color="teal lighten-3" text-color="teal darken-4" label class="font-weight-bold">
            {{ sortedFechasProyectadas.length }} fecha(s)
          </v-chip>
        </v-card-title>

        <v-card-text class="pa-4">
          <div v-if="loadingFechas" class="text-center py-4">
            <v-progress-circular indeterminate color="teal" />
          </div>

          <div v-else-if="sortedFechasProyectadas.length === 0" class="text-center grey--text py-4">
            <v-icon large color="grey lighten-2">mdi-calendar-remove</v-icon>
            <div class="mt-2 caption">No hay fechas proyectadas registradas para este programa.</div>
          </div>

          <v-timeline v-else dense clipped>
            <v-timeline-item
              v-for="fecha in sortedFechasProyectadas"
              :key="fecha.id"
              :color="esPasado(fecha.fecha_proyectada) ? 'grey lighten-1' : 'teal darken-1'"
              small
            >
              <v-row align="center" no-gutters>
                <v-col cols="12" sm="5">
                  <span class="body-2 font-weight-medium" :class="esPasado(fecha.fecha_proyectada) ? 'grey--text' : ''">
                    {{ traducirTipoFecha(fecha.tipo_fecha) }}
                  </span>
                  <div class="caption grey--text">{{ fecha.fecha_proyectada }}</div>
                </v-col>
                <v-col cols="6" sm="4">
                  <v-chip
                    v-if="fecha.estado_destino_codigo"
                    x-small
                    :color="colorEstadoDestino(fecha.estado_destino_codigo)"
                    dark
                  >
                    {{ fecha.estado_destino_codigo }}
                  </v-chip>
                </v-col>
                <v-col cols="6" sm="3" class="text-right">
                  <span class="caption grey--text">Día {{ fecha.dias_desde_matricula }}</span>
                </v-col>
              </v-row>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
      </v-card>


      <v-progress-linear v-if="loading" indeterminate class="mb-4" />

      <!-- MÓDULOS: CONFIGURACIÓN + FECHAS ESTIMADAS (tabla unificada) -->
      <v-row>
        <v-col cols="12">
          <v-card outlined class="rounded-lg">
            <v-card-title class="subtitle-1 font-weight-bold py-3 px-4 blue darken-3 white--text">
              <v-icon left color="white">mdi-view-list-outline</v-icon>
              Módulos — Configuración y Fechas Estimadas
              <v-spacer />
              <v-chip v-if="fechaInicioModulo0" x-small color="blue lighten-4" text-color="blue darken-4" label class="font-weight-bold mr-3">
                <v-icon left x-small>mdi-calendar-start</v-icon>
                Base: {{ formatFecha(fechaInicioModulo0) }}
              </v-chip>
              <v-btn-toggle v-model="vistaCalendario" mandatory dense rounded color="white">
                <v-btn :value="false" x-small>
                  <v-icon x-small>mdi-table</v-icon>
                </v-btn>
                <v-btn :value="true" x-small :disabled="!modulosConFechasCalculadas.length">
                  <v-icon x-small>mdi-calendar-month</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-card-title>
            <v-divider />

            <!-- BARRA DE CONFIGURACIÓN GLOBAL -->
            <div class="px-4 py-3" style="background: #f8f9fa; border-bottom: 1px solid #e0e0e0">
              <v-row dense align="start">

                <!-- Paso 1: Días por semana -->
                <v-col cols="12" sm="auto" style="min-width: 180px">
                  <div class="d-flex align-center mb-2">
                    <v-avatar color="blue darken-3" size="18" class="mr-1 flex-shrink-0">
                      <span style="font-size:10px;color:white;font-weight:bold">1</span>
                    </v-avatar>
                    <span class="caption font-weight-medium blue--text text--darken-2">Clases por semana</span>
                  </div>
                  <v-btn-toggle v-model="config.dias_clases_semana" mandatory color="blue darken-2" rounded dense>
                    <v-btn v-for="n in 6" :key="n" :value="n" small>{{ n }}</v-btn>
                  </v-btn-toggle>
                </v-col>

                <v-divider vertical class="mx-3 mt-5 hidden-xs-only" style="height:48px; align-self:flex-start" />

                <!-- Paso 2: Días de la semana -->
                <v-col cols="12" sm="auto" style="min-width: 220px">
                  <div class="d-flex align-center mb-2">
                    <v-avatar color="blue darken-3" size="18" class="mr-1 flex-shrink-0">
                      <span style="font-size:10px;color:white;font-weight:bold">2</span>
                    </v-avatar>
                    <span class="caption font-weight-medium blue--text text--darken-2">Días de clase</span>
                    <v-icon
                      v-if="config.dias_semana.length === config.dias_clases_semana && config.dias_semana.length > 0"
                      x-small color="green darken-2" class="ml-1"
                    >mdi-check-circle</v-icon>
                  </div>
                  <div class="d-flex flex-wrap" style="gap: 4px">
                    <v-chip
                      v-for="dia in diasSemanaOpciones"
                      :key="dia.valor"
                      :color="config.dias_semana.includes(dia.valor) ? 'blue darken-2' : 'grey lighten-2'"
                      :text-color="config.dias_semana.includes(dia.valor) ? 'white' : 'grey darken-2'"
                      small
                      style="cursor: pointer; min-width: 38px; justify-content: center"
                      @click="toggleDia(dia.valor)"
                    >{{ dia.label }}</v-chip>
                  </div>
                  <div v-if="config.dias_semana.length < config.dias_clases_semana" class="d-flex align-center mt-1">
                    <v-icon x-small color="orange darken-2" class="mr-1">mdi-alert</v-icon>
                    <span class="caption orange--text text--darken-2">Selecciona {{ config.dias_clases_semana - config.dias_semana.length }} día(s) más</span>
                  </div>
                </v-col>

                <v-divider vertical class="mx-3 mt-5 hidden-xs-only" style="height:48px; align-self:flex-start" />

                <!-- Paso 3: Aplicar a todos -->
                <v-col cols="12" sm="auto">
                  <div class="d-flex align-center mb-2">
                    <v-avatar color="blue-grey darken-1" size="18" class="mr-1 flex-shrink-0">
                      <span style="font-size:10px;color:white;font-weight:bold">3</span>
                    </v-avatar>
                    <span class="caption font-weight-medium blue-grey--text text--darken-2">Aplicar a todos los módulos</span>
                  </div>
                  <div class="d-flex align-center flex-wrap" style="gap: 8px">
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="aplicarATodos.duracion_semanas"
                        type="number" min="1" max="52"
                        dense outlined hide-details suffix="sem." label="Duración"
                        style="max-width: 108px"
                      />
                      <v-btn small color="blue darken-2" dark class="ml-1" @click="aplicarDuracionATodos" title="Aplicar duración a todos los módulos">
                        <v-icon x-small>mdi-transfer-down</v-icon>
                      </v-btn>
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="aplicarATodos.receso_dias"
                        type="number" min="0" max="60"
                        dense outlined hide-details suffix="días" label="Receso"
                        style="max-width: 108px"
                      />
                      <v-btn small color="blue-grey darken-1" dark class="ml-1" @click="aplicarRecesoATodos" title="Aplicar receso a todos los módulos">
                        <v-icon x-small>mdi-transfer-down</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
            <v-divider />

            <!-- Banner de estado de configuración -->
            <div
              v-if="programaInfo && configuracionEstado !== 'completo'"
              class="px-4 py-3 d-flex align-center flex-wrap"
              :style="{ background: configuracionEstado === 'parcial' ? '#fff3e0' : '#fce4ec' }"
              style="gap: 8px"
            >
              <v-icon :color="configuracionEstado === 'parcial' ? 'orange darken-2' : 'red darken-2'" small>
                {{ configuracionEstado === 'parcial' ? 'mdi-alert-circle' : 'mdi-close-circle' }}
              </v-icon>
              <span class="caption font-weight-medium" :class="configuracionEstado === 'parcial' ? 'orange--text text--darken-3' : 'red--text text--darken-2'">
                {{ configuracionEstado === 'parcial' ? 'Configuración incompleta' : 'Sin configurar' }} —
                falta: {{ configuracionFaltante.join(' · ') }}
              </span>
            </div>
            <div
              v-else-if="programaInfo && configuracionEstado === 'completo'"
              class="px-4 py-2 d-flex align-center"
              style="background: #e8f5e9; gap: 6px"
            >
              <v-icon color="green darken-2" small>mdi-check-circle</v-icon>
              <span class="caption green--text text--darken-2 font-weight-medium">Configuración completa</span>
            </div>

            <!-- Sin módulos -->
            <div v-if="modulos.length === 0 && !loading" class="pa-6 text-center grey--text">
              <v-icon large color="grey lighten-2">mdi-inbox-outline</v-icon>
              <div class="mt-2">No hay módulos cargados para este programa.</div>
            </div>

            <!-- Aviso sin fecha base -->
            <div v-else-if="!fechaInicioModulo0 || !config.dias_semana.length" class="px-4 py-2 caption blue-grey--text" style="background: #eceff1">
              <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
              Las columnas de <strong>Inicio</strong> y <strong>Fin estimado</strong> aparecerán cuando estén disponibles la fecha de Inicio posible de Módulo 0 y los días de clase configurados.
            </div>

            <!-- Tabla unificada -->
            <div v-show="!vistaCalendario" style="overflow-x: auto">
              <v-simple-table v-if="modulos.length > 0" dense>
                <thead style="background: #1565c0">
                  <tr>
                    <th class="white--text text-center" style="width: 52px">N°</th>
                    <th class="white--text">Módulo</th>
                    <th class="white--text text-center" style="width: 140px">Duración (sem.)</th>
                    <th class="white--text text-center" style="width: 140px">
                      Receso (días)
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon x-small color="blue lighten-3" v-on="on">mdi-help-circle-outline</v-icon>
                        </template>
                        <span>Días de descanso antes del módulo siguiente.<br>El último módulo no tiene receso.</span>
                      </v-tooltip>
                    </th>
                    <th class="white--text text-center" style="width: 90px">Días clase</th>
                    <th class="white--text text-center" style="width: 120px">
                      <v-icon x-small color="blue lighten-3" class="mr-1">mdi-calendar-start</v-icon>Inicio est.
                    </th>
                    <th class="white--text text-center" style="width: 120px">
                      <v-icon x-small color="blue lighten-3" class="mr-1">mdi-calendar-end</v-icon>Fin est.
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(mod, idx) in modulos">
                    <tr
                      :key="'mod-' + mod.numero"
                      :style="mod.duracion_semanas === 0 ? 'background: #fff8e1' : ''"
                    >
                      <td class="text-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-chip
                              x-small
                              :color="mod.duracion_semanas > 0 ? 'blue darken-3' : 'orange darken-2'"
                              dark
                              v-on="on"
                            >{{ mod.numero }}</v-chip>
                          </template>
                          <span>{{ mod.duracion_semanas > 0 ? 'Módulo configurado' : 'Sin duración configurada' }}</span>
                        </v-tooltip>
                      </td>
                      <td class="py-2 body-2">
                        {{ mod.nombre }}
                        <v-icon v-if="mod.duracion_semanas === 0" x-small color="orange darken-2" class="ml-1">mdi-alert-circle-outline</v-icon>
                      </td>

                      <!-- Duración -->
                      <td class="text-center py-1">
                        <v-text-field
                          v-model.number="mod.duracion_semanas"
                          type="number" min="1" max="52" dense outlined hide-details
                          style="max-width: 100px; margin: auto"
                          @change="clampModulo(mod, 'duracion_semanas', 1, 52)"
                        />
                      </td>

                      <!-- Receso -->
                      <td class="text-center py-1">
                        <v-tooltip bottom :disabled="idx !== modulos.length - 1">
                          <template v-slot:activator="{ on }">
                            <div v-on="on" style="display: inline-block">
                              <v-text-field
                                v-model.number="mod.receso_dias"
                                type="number" min="0" max="60" dense outlined hide-details
                                :disabled="idx === modulos.length - 1"
                                style="max-width: 100px; margin: auto"
                                @change="clampModulo(mod, 'receso_dias', 0, 60)"
                              />
                            </div>
                          </template>
                          <span>Último módulo — sin receso posterior</span>
                        </v-tooltip>
                      </td>

                      <!-- Días clase -->
                      <td class="text-center grey--text body-2">{{ calcularDiasModulo(mod) }}</td>

                      <!-- Fechas estimadas -->
                      <td class="text-center body-2 font-weight-medium" :class="fechaModulo(mod.numero).fecha_inicio ? 'indigo--text text--darken-2' : 'grey--text'">
                        {{ formatFecha(fechaModulo(mod.numero).fecha_inicio) }}
                      </td>
                      <td class="text-center body-2 font-weight-medium" :class="fechaModulo(mod.numero).fecha_fin ? 'indigo--text text--darken-2' : 'grey--text'">
                        {{ formatFecha(fechaModulo(mod.numero).fecha_fin) }}
                      </td>
                    </tr>

                    <!-- Fila de receso entre módulos -->
                    <tr
                      v-if="idx !== modulos.length - 1 && mod.receso_dias > 0 && fechaModulo(mod.numero).fecha_fin"
                      :key="'receso-' + mod.numero"
                      style="background: #e8eaf6"
                    >
                      <td colspan="7" class="caption indigo--text text--darken-3 py-1 px-4">
                        <v-icon x-small color="indigo lighten-2" class="mr-1">mdi-clock-outline</v-icon>
                        Receso {{ mod.receso_dias }} día(s) — del
                        {{ formatFecha(addDiasStr(fechaModulo(mod.numero).fecha_fin, 1)) }} al
                        {{ formatFecha(addDiasStr(fechaModulo(mod.numero).fecha_fin, mod.receso_dias)) }}
                      </td>
                    </tr>
                  </template>
                </tbody>
              </v-simple-table>
            </div>

            <!-- Vista Gantt (timeline completo) -->
            <div v-show="vistaCalendario" style="overflow-x: auto">
              <div class="pa-4" style="min-width: 560px">

                <div v-if="!modulosConFechasCalculadas.length" class="text-center grey--text py-6">
                  <v-icon large color="grey lighten-2">mdi-calendar-question</v-icon>
                  <div class="mt-2 caption">Se requiere la fecha de Inicio posible de Módulo 0 y los días configurados.</div>
                </div>

                <template v-else>
                  <!-- Encabezado de meses -->
                  <div class="d-flex mb-1">
                    <div style="width: 180px; min-width: 180px; flex-shrink: 0"></div>
                    <div class="flex-grow-1" style="position: relative">
                      <div v-if="todayOffset" style="position: absolute; top: 0; bottom: 0; width: 1px; background: #f44336; z-index: 5; pointer-events: none" :style="{ left: todayOffset }">
                        <span style="position: absolute; top: 0; left: 3px; font-size: 9px; color: #f44336; white-space: nowrap; font-weight: bold">Hoy</span>
                      </div>
                      <div class="d-flex" style="border-bottom: 2px solid #1565c0">
                        <div
                          v-for="mes in mesesTimeline"
                          :key="mes.label"
                          class="caption text-center font-weight-medium blue--text text--darken-3"
                          style="overflow: hidden; white-space: nowrap; padding: 2px 2px; border-right: 1px solid #bbdefb; flex-shrink: 0"
                          :style="{ width: (mes.dias / totalDiasPrograma * 100) + '%' }"
                        >
                          {{ mes.label }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Filas de eventos -->
                  <div
                    v-for="evento in eventosTimeline"
                    :key="evento.key"
                    class="d-flex align-center"
                    :style="{ height: evento.esReceso ? '22px' : '36px' }"
                  >
                    <!-- Etiqueta -->
                    <div
                      style="width: 180px; min-width: 180px; flex-shrink: 0; padding-right: 8px"
                      class="caption text-truncate"
                      :class="evento.esReceso ? 'grey--text' : 'grey--text text--darken-3 font-weight-medium'"
                      :title="evento.nombre"
                    >
                      {{ evento.nombre }}
                    </div>

                    <!-- Barra de timeline -->
                    <div class="flex-grow-1" style="position: relative" :style="{ height: evento.esReceso ? '12px' : '26px' }">
                      <!-- Línea de hoy (en cada fila para efecto continuo) -->
                      <div
                        v-if="todayOffset"
                        style="position: absolute; top: -5px; bottom: -5px; width: 1px; background: rgba(244,67,54,0.25); z-index: 2; pointer-events: none"
                        :style="{ left: todayOffset }"
                      ></div>
                      <!-- Barra -->
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <div
                            v-on="on"
                            :style="{
                              position: 'absolute',
                              left: evento.left,
                              width: evento.width,
                              height: '100%',
                              background: evento.color,
                              borderRadius: '4px',
                              minWidth: '3px',
                              cursor: 'default',
                            }"
                            class="d-flex align-center px-1"
                          >
                            <span
                              v-if="evento.widthNum > 7"
                              style="font-size: 10px; color: white; overflow: hidden; white-space: nowrap; line-height: 1"
                            >
                              {{ formatFecha(evento.inicio) }} – {{ formatFecha(evento.fin) }}
                            </span>
                          </div>
                        </template>
                        <div class="caption">
                          <strong>{{ evento.nombre }}</strong><br>
                          Inicio: {{ formatFecha(evento.inicio) }}<br>
                          Fin:&nbsp;&nbsp;&nbsp;&nbsp;{{ formatFecha(evento.fin) }}
                        </div>
                      </v-tooltip>
                    </div>
                  </div>

                  <!-- Leyenda -->
                  <div class="d-flex flex-wrap mt-4" style="gap: 12px; border-top: 1px solid #e0e0e0; padding-top: 10px">
                    <div v-for="(mod, idx) in modulosConFechasCalculadas" :key="'leg-' + idx" class="d-flex align-center caption">
                      <div class="rounded mr-1" :style="{ width: '12px', height: '12px', background: leyendaColor(idx) }"></div>
                      <span class="grey--text text--darken-2">{{ mod.nombre }}</span>
                    </div>
                    <div class="d-flex align-center caption">
                      <div class="rounded mr-1" style="width:12px;height:12px;background:#bdbdbd"></div>
                      <span class="grey--text">Receso</span>
                    </div>
                    <v-spacer />
                    <div v-if="todayOffset" class="d-flex align-center caption red--text">
                      <div class="mr-1" style="width:14px;height:2px;background:#f44336;display:inline-block;border-radius:1px"></div>
                      Hoy
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Resumen footer -->
            <v-divider v-if="modulos.length > 0" />
            <div v-if="modulos.length > 0" class="pa-3 d-flex justify-space-between align-center flex-wrap" style="gap: 8px">
              <span class="caption grey--text">
                <template v-if="modulosConFechasCalculadas.length">
                  <v-icon x-small class="mr-1">mdi-calendar-start</v-icon>
                  Inicio: <strong>{{ formatFecha(modulosConFechasCalculadas[0].fecha_inicio) }}</strong>
                  &nbsp;·&nbsp;
                  <v-icon x-small class="mr-1">mdi-calendar-end</v-icon>
                  Fin: <strong>{{ formatFecha(modulosConFechasCalculadas[modulosConFechasCalculadas.length - 1].fecha_fin) }}</strong>
                  &nbsp;·&nbsp;
                </template>
                Duración total estimada:
              </span>
              <v-chip color="blue darken-3" dark small>
                {{ totalSemanasPrograma }} sem. — {{ totalDiasClasesPrograma }} días de clase + {{ totalDiasReceso }} días de receso
              </v-chip>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- BROCHURE -->
      <v-row class="mt-4">
        <v-col cols="12" md="8" offset-md="2">
          <v-card outlined class="rounded-lg">
            <v-card-title class="py-3 teal darken-1 white--text subtitle-1 font-weight-bold">
              <v-icon left color="white">mdi-file-pdf-box</v-icon>
              Brochure del Programa
              <v-spacer />
              <v-chip v-if="brochureArchivoId" x-small color="green lighten-2" text-color="white" label class="font-weight-bold">
                <v-icon left x-small>mdi-check-circle</v-icon> Cargado
              </v-chip>
              <v-chip v-else x-small color="orange lighten-2" text-color="white" label>Sin brochure</v-chip>
            </v-card-title>

            <v-card-text class="pa-4">
              <v-alert v-if="brochureArchivoId" dense type="info" text class="mb-3">
                <strong>Archivo ID:</strong> {{ brochureArchivoId }}
              </v-alert>

              <v-file-input
                v-model="archivoBrochure"
                accept="application/pdf"
                :disabled="cargandoBrochure"
                label="Seleccione el brochure (PDF)"
                prepend-icon="mdi-file-pdf-box"
                dense
                outlined
                hide-details
                @change="brochureSelect"
                class="mb-3"
              />

              <v-alert v-if="msgBrochure" dense type="error" text class="mt-2 mb-0">{{ msgBrochure }}</v-alert>
              <v-alert v-if="subidoresBrochure" dense type="success" text class="mt-3 mb-0">
                Brochure subido correctamente
              </v-alert>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
              <v-btn
                :disabled="brochuresel"
                :loading="cargandoBrochure"
                color="teal darken-1"
                small
                dark
                @click="subirBrochure"
              >
                <v-icon left small>mdi-upload</v-icon> Subir Brochure
              </v-btn>

              <v-btn
                v-if="brochureArchivoId"
                :loading="cargandoBrochure"
                color="primary"
                small
                outlined
                class="ml-2"
                @click="verBrochure"
              >
                <v-icon left small>mdi-eye</v-icon> Ver / Descargar
              </v-btn>

              <v-spacer />

              <v-btn
                v-if="brochureArchivoId"
                :loading="eliminandoBrochure"
                color="error"
                small
                outlined
                @click="eliminarBrochure"
              >
                <v-icon left small>mdi-delete</v-icon> Eliminar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- ANTICIPO DE MÓDULO -->
      <SolicitudAnticipoModulo
        ref="anticipo"
        :programa-id="programaId"
        :programa-nombre="programa ? programa.nombre_compuesto : ''"
        class="mt-4"
      />

      <!-- DADO DE BAJA -->
      <SolicitudDadoDeBaja
        :programa-id="programaId"
        :estado-programa-codigo="estado_programa_codigo"
        :inscritos-efectivos="programaInfo ? (programaInfo.inscritos_efectivos || 0) : 0"
        class="mt-2"
        @baja-aprobada="cargarPrograma"
      />

      <!-- HISTORIAL SOLICITUDES DE CONFIGURACIÓN -->
      <v-card v-if="historialSolicitudes.length > 0 || loadingHistorialConfig" outlined class="mt-4 rounded-lg">
        <v-card-title class="subtitle-2 font-weight-bold py-3 px-4 blue-grey darken-2 white--text">
          <v-icon left color="white">mdi-history</v-icon>
          Solicitudes de Cambio de Configuración
          <v-spacer />
          <v-progress-circular v-if="loadingHistorialConfig" indeterminate color="white" size="16" width="2" />
          <v-chip v-else x-small color="white" class="blue-grey--text text--darken-2 font-weight-bold">
            {{ historialSolicitudes.length }}
          </v-chip>
        </v-card-title>
        <v-list dense two-line class="pa-0">
          <v-list-item
            v-for="sol in historialSolicitudes"
            :key="sol.id"
            style="border-bottom: 1px solid #e0e0e0"
          >
            <v-list-item-content>
              <v-list-item-title class="body-2 font-weight-medium">
                {{ sol.descripcion || 'Sin descripción' }}
              </v-list-item-title>
              <v-list-item-subtitle class="caption">
                {{ sol.solicitante_nombres }} {{ sol.solicitante_primer_apellido }}
                — {{ (sol.fecha_registro || '').slice(0, 10) }}
                <span v-if="sol.respuesta"> · <em>{{ sol.respuesta }}</em></span>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <div class="d-flex align-center" style="gap: 6px">
                <v-chip x-small dark
                  :color="sol.estado === 'APROBADO' ? 'green darken-1' : sol.estado === 'RECHAZADO' ? 'red darken-1' : 'orange darken-1'"
                >{{ sol.estado }}</v-chip>
                <template v-if="esAutorizante && sol.estado === 'SOLICITADO'">
                  <v-btn x-small color="green darken-1" dark :loading="loadingRespuesta === sol.id" @click="aprobarSolicitud(sol)">
                    <v-icon x-small left>mdi-check</v-icon> Aprobar
                  </v-btn>
                  <v-btn x-small color="red darken-1" outlined :disabled="loadingRespuesta !== null" @click="abrirRechazar(sol)">
                    <v-icon x-small left>mdi-close</v-icon> Rechazar
                  </v-btn>
                </template>
              </div>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>

      <!-- ACCIONES -->
      <v-row class="mt-4">
        <v-col cols="12">
          <!-- Solicitud pendiente — bloquea mientras no se resuelva -->
          <v-alert v-if="solicitudPendiente" type="warning" dense outlined class="mb-3">
            <strong>Solicitud pendiente de aprobación</strong>
            <span v-if="solicitudPendiente.descripcion"> — {{ solicitudPendiente.descripcion }}</span>
            <div class="caption mt-1">
              Enviada el {{ (solicitudPendiente.fecha_registro || '').slice(0, 10) }} · esperando aprobación de un Académico.
            </div>
          </v-alert>

          <!-- Motivo para solicitar (cuando hay historial y no hay pendiente activa) -->
          <v-textarea
            v-if="tieneHistorialConfig && !solicitudPendiente"
            v-model="descripcionSolicitud"
            label="Motivo del cambio de configuración"
            outlined dense rows="2" auto-grow
            class="mb-3"
            hint="Obligatorio para enviar la solicitud"
          />

          <div class="d-flex justify-end" style="gap: 10px">
            <v-btn outlined @click="resetForm" :disabled="loading || saving || savingSolicitud">
              <v-icon left>mdi-restore</v-icon> Restaurar
            </v-btn>

            <!-- Guardar directo: primera vez o autorizante (edita sin solicitar) -->
            <v-btn
              v-if="!tieneHistorialConfig || esAutorizante"
              color="blue darken-3" dark
              @click="guardar"
              :loading="saving"
              :disabled="loading || config.dias_semana.length < config.dias_clases_semana"
            >
              <v-icon left>mdi-content-save-outline</v-icon> Guardar Configuración
            </v-btn>

            <!-- Solicitar cambio:
                 · autorizante con historial (opcional, para dejar registro)
                 · no autorizante con historial y sin pendiente -->
            <v-btn
              v-if="tieneHistorialConfig && !solicitudPendiente"
              color="orange darken-2" dark
              @click="solicitarCambio"
              :loading="savingSolicitud"
              :disabled="loading || config.dias_semana.length < config.dias_clases_semana"
            >
              <v-icon left>mdi-send-outline</v-icon> Solicitar Cambio
            </v-btn>

            <!-- Bloqueado por solicitud pendiente (solo no autorizantes) -->
            <v-chip v-if="tieneHistorialConfig && !esAutorizante && solicitudPendiente" color="orange darken-1" dark label class="font-weight-bold">
              <v-icon left small>mdi-clock-outline</v-icon> Pendiente de aprobación
            </v-chip>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- DIALOG BROCHURE -->
    <v-dialog v-model="dialogBrochure" max-width="960" persistent>
      <v-card>
        <v-toolbar color="teal darken-1" dark dense flat>
          <v-icon class="mr-2">mdi-file-pdf-box</v-icon>
          <v-toolbar-title class="font-weight-bold">
            Brochure — {{ programa ? programa.nombre_compuesto : '' }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="descargarBrochure" title="Descargar PDF">
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn icon @click="cerrarDialogBrochure" title="Cerrar">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-0">
          <div v-if="cargandoVisualizacion" class="d-flex justify-center align-center" style="height: 500px">
            <v-progress-circular indeterminate color="teal" size="48" />
          </div>
          <iframe
            v-else-if="brochureDataUri"
            :src="brochureDataUri"
            style="width: 100%; height: 75vh; border: none; display: block"
            title="Brochure del programa"
          />
        </v-card-text>

        <v-divider />
        <v-card-actions class="justify-end">
          <v-btn color="primary" outlined small @click="descargarBrochure">
            <v-icon left small>mdi-download</v-icon> Descargar PDF
          </v-btn>
          <v-btn color="grey darken-1" text small @click="cerrarDialogBrochure">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG DECIDIR (Arrastre Crítico) -->
    <v-dialog v-model="dialogDecision" max-width="500" persistent>
      <v-card>
        <v-toolbar color="red darken-2" dark dense flat>
          <v-icon class="mr-2">mdi-gavel</v-icon>
          <v-toolbar-title>Decisión — Arrastre Crítico</v-toolbar-title>
          <v-spacer />
          <v-btn icon :disabled="loadingDecision" @click="dialogDecision = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-alert type="warning" dense outlined class="mb-4">
            El programa está en <strong>Arrastre Crítico</strong>. Debes elegir una decisión.
          </v-alert>
          <v-radio-group v-model="decisionTipo" class="mt-0">
            <v-radio value="BAJA" label="Dar de baja el programa" color="red darken-2" />
            <v-radio value="DESARROLLO_SIN_PE" label="Continuar sin punto de equilibrio" color="orange darken-2" />
          </v-radio-group>
          <v-textarea
            v-model="decisionDescripcion"
            label="Motivo / observación (opcional)"
            outlined dense rows="3" auto-grow
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loadingDecision" @click="dialogDecision = false">Cancelar</v-btn>
          <v-btn
            color="red darken-2" dark
            :disabled="!decisionTipo" :loading="loadingDecision"
            @click="enviarDecision"
          >Confirmar decisión</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG ERROR ANTICIPO -->
    <v-dialog v-model="dialogErrorAnticipo" max-width="520">
      <v-card>
        <v-toolbar color="orange darken-3" dark dense flat>
          <v-icon class="mr-2">mdi-alert-circle</v-icon>
          <v-toolbar-title>No se puede adelantar la fecha</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <p class="body-2">{{ errorAnticipoMsg }}</p>
          <p class="caption grey--text">Puedes solicitar una excepción de anticipo en la sección de más abajo.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogErrorAnticipo = false">Entendido</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG RECHAZAR SOLICITUD CONFIGURACIÓN -->
    <v-dialog v-model="dialogRechazar" max-width="500" persistent>
      <v-card>
        <v-toolbar color="red darken-2" dark dense flat>
          <v-icon class="mr-2">mdi-close-circle</v-icon>
          <v-toolbar-title>Rechazar solicitud</v-toolbar-title>
          <v-spacer />
          <v-btn icon :disabled="loadingRespuesta !== null" @click="dialogRechazar = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <p v-if="solicitudAgestionar" class="body-2 mb-3">
            <strong>Solicitud:</strong> {{ solicitudAgestionar.descripcion || 'Sin descripción' }}
          </p>
          <v-textarea
            v-model="formRechazar.respuesta"
            label="Motivo del rechazo"
            outlined dense rows="3" auto-grow
            hint="Requerido"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="loadingRespuesta !== null" @click="dialogRechazar = false">Cancelar</v-btn>
          <v-btn
            color="red darken-2" dark
            :disabled="!formRechazar.respuesta"
            :loading="loadingRespuesta !== null"
            @click="confirmarRechazar"
          >Rechazar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SNACKBAR -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="3500">
      {{ snackbar.msg }}
      <template v-slot:action>
        <v-btn text small @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import xaxios from '@/xaxios';
import { parseModalidadInverse, parseFasePrograma } from '@/components/Utils';
import SolicitudAnticipoModulo from './SolicitudAnticipoModulo.vue';
import SolicitudDadoDeBaja from './SolicitudDadoDeBaja.vue';

const DIAS_NUM = { DOMINGO: 0, LUNES: 1, MARTES: 2, MIERCOLES: 3, JUEVES: 4, VIERNES: 5, SABADO: 6 };

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function dateToStr(date) {
  return date.toISOString().slice(0, 10);
}

const DIAS_SEMANA = [
  { valor: 'LUNES',     label: 'Lun' },
  { valor: 'MARTES',    label: 'Mar' },
  { valor: 'MIERCOLES', label: 'Mié' },
  { valor: 'JUEVES',    label: 'Jue' },
  { valor: 'VIERNES',   label: 'Vie' },
  { valor: 'SABADO',    label: 'Sáb' },
  { valor: 'DOMINGO',   label: 'Dom' },
];

const DEFAULT_CONFIG = () => ({
  dias_clases_semana: 2,
  dias_semana: ['SABADO'],
});

export default {
  name: 'ConfiguracionClasesPrograma',

  components: { SolicitudAnticipoModulo, SolicitudDadoDeBaja },

  data() {
    return {
      programa: null,
      programaInfo: null,
      estado_programa_codigo: null,
      dialogDecision: false,
      decisionTipo: null,
      decisionDescripcion: '',
      loadingDecision: false,
      dialogErrorAnticipo: false,
      errorAnticipoMsg: '',
      tieneHistorialConfig: false,
      loadingHistorialConfig: false,
      historialSolicitudes: [],
      solicitudPendiente: null,
      descripcionSolicitud: '',
      savingSolicitud: false,
      dialogRechazar: false,
      solicitudAgestionar: null,
      formRechazar: { respuesta: '' },
      loadingRespuesta: null,
      modulos: [],
      config: DEFAULT_CONFIG(),
      configOriginal: null,
      loading: false,
      saving: false,
      diasSemanaOpciones: DIAS_SEMANA,
      aplicarATodos: {
        duracion_semanas: 4,
        receso_dias: 7,
      },
      fechasProyectadas: [],
      loadingFechas: false,
      vistaCalendario: false,
      archivoBrochure: null,
      cargandoBrochure: false,
      eliminandoBrochure: false,
      brochuresel: true,
      subidoresBrochure: false,
      brochureArchivoId: null,
      msgBrochure: '',
      dialogBrochure: false,
      brochureDataUri: null,
      cargandoVisualizacion: false,
      snackbar: { show: false, msg: '', color: 'success' },
    };
  },

  watch: {
    'config.dias_clases_semana'(nuevo) {
      if (this.config.dias_semana.length > nuevo) {
        this.config.dias_semana = this.config.dias_semana.slice(0, nuevo);
      }
    },
  },

  computed: {
    programaId() {
      return this.$route.params.id;
    },

    esAutorizante() {
      const cargo = this.$store.state.CARGO;
      return cargo && [1, 5, 6, 17].includes(cargo.id);
    },

    sortedFechasProyectadas() {
      return [...this.fechasProyectadas].sort((a, b) => a.dias_desde_matricula - b.dias_desde_matricula);
    },

    fechaInicioModulo0() {
      const f = this.fechasProyectadas.find((fp) => fp.tipo_fecha === 'INICIO_MODULO_0');
      return f ? f.fecha_proyectada : null;
    },

    modulosConFechasCalculadas() {
      const base = this.fechaInicioModulo0;
      const dias = this.config.dias_semana;
      if (!base || !this.modulos.length || !dias.length) return [];

      const diasNums = dias.map((d) => DIAS_NUM[d]).sort((a, b) => a - b);

      const nextClassDay = (fromDate) => {
        const d = new Date(fromDate + 'T00:00:00');
        for (let i = 0; i < 7; i++) {
          if (diasNums.includes((d.getDay() + i) % 7)) return addDays(d, i);
        }
        return d;
      };

      const lastClassDayInPeriod = (startDate, duracionSemanas) => {
        const periodEnd = addDays(startDate, (duracionSemanas || 1) * 7 - 1);
        for (let i = 0; i < 7; i++) {
          const candidate = addDays(periodEnd, -i);
          if (diasNums.includes(candidate.getDay())) return candidate;
        }
        return periodEnd;
      };

      const resultado = [];
      let fechaActual = nextClassDay(base);

      for (let i = 0; i < this.modulos.length; i++) {
        const mod = this.modulos[i];
        const inicio = new Date(fechaActual);
        const fin = lastClassDayInPeriod(inicio, mod.duracion_semanas || 1);
        const receso = i < this.modulos.length - 1 ? (mod.receso_dias || 0) : 0;

        resultado.push({
          numero: mod.numero,
          nombre: mod.nombre,
          fecha_inicio: dateToStr(inicio),
          fecha_fin: dateToStr(fin),
          duracion_semanas: mod.duracion_semanas,
          receso_dias: receso,
        });

        fechaActual = nextClassDay(dateToStr(addDays(fin, receso + 1)));
      }

      return resultado;
    },

    configuracionEstado() {
      if (!this.programaInfo) return 'pendiente';
      const tieneDias = !!(this.programaInfo.dias_clases_semana) &&
        Array.isArray(this.programaInfo.dias_semana) && this.programaInfo.dias_semana.length > 0;
      const mods = this.programaInfo.programa_modulo || [];
      const modulosConfigurados = mods.length > 0 && mods.every((m) => m.duracion_semanas > 0);
      if (tieneDias && modulosConfigurados) return 'completo';
      if (tieneDias || modulosConfigurados) return 'parcial';
      return 'pendiente';
    },

    configuracionFaltante() {
      const falta = [];
      if (!this.programaInfo) return falta;
      if (!this.programaInfo.dias_clases_semana) falta.push('Días de clases por semana');
      if (!Array.isArray(this.programaInfo.dias_semana) || !this.programaInfo.dias_semana.length) {
        falta.push('Días de la semana');
      }
      const mods = this.programaInfo.programa_modulo || [];
      const sinDuracion = mods.filter((m) => !m.duracion_semanas || m.duracion_semanas === 0);
      if (sinDuracion.length) falta.push(`Duración de ${sinDuracion.length} módulo(s)`);
      return falta;
    },

    programaModulosOrdenados() {
      if (!this.programaInfo?.programa_modulo) return [];
      return [...this.programaInfo.programa_modulo].sort((a, b) => a.numero - b.numero);
    },

    programaFechaInicio() {
      return this.modulosConFechasCalculadas[0]?.fecha_inicio || null;
    },

    programaFechaFin() {
      const last = this.modulosConFechasCalculadas[this.modulosConFechasCalculadas.length - 1];
      return last?.fecha_fin || null;
    },

    totalDiasPrograma() {
      if (!this.programaFechaInicio || !this.programaFechaFin) return 1;
      const ms = new Date(this.programaFechaFin + 'T00:00:00') - new Date(this.programaFechaInicio + 'T00:00:00');
      return Math.round(ms / 86400000) + 1;
    },

    mesesTimeline() {
      if (!this.programaFechaInicio || !this.programaFechaFin) return [];
      const progStart = new Date(this.programaFechaInicio + 'T00:00:00');
      const progEnd = new Date(this.programaFechaFin + 'T00:00:00');
      const meses = [];
      let cur = new Date(progStart.getFullYear(), progStart.getMonth(), 1);
      while (cur <= progEnd) {
        const mesStart = new Date(Math.max(cur.getTime(), progStart.getTime()));
        const mesEnd = new Date(Math.min(
          new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getTime(),
          progEnd.getTime()
        ));
        meses.push({
          label: cur.toLocaleDateString('es-BO', { month: 'short', year: '2-digit' }),
          dias: Math.round((mesEnd - mesStart) / 86400000) + 1,
        });
        cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      }
      return meses;
    },

    eventosTimeline() {
      if (!this.programaFechaInicio || !this.totalDiasPrograma) return [];
      const COLORES = ['#1565c0','#283593','#6a1b9a','#00695c','#2e7d32','#00838f','#4527a0','#37474f'];
      const base = new Date(this.programaFechaInicio + 'T00:00:00');
      const total = this.totalDiasPrograma;
      const eventos = [];
      this.modulosConFechasCalculadas.forEach((mod, idx) => {
        const mStart = new Date(mod.fecha_inicio + 'T00:00:00');
        const mEnd = new Date(mod.fecha_fin + 'T00:00:00');
        const offset = Math.round((mStart - base) / 86400000);
        const duracion = Math.round((mEnd - mStart) / 86400000) + 1;
        eventos.push({
          key: 'mod-' + mod.numero,
          nombre: `M${mod.numero}: ${mod.nombre}`,
          inicio: mod.fecha_inicio,
          fin: mod.fecha_fin,
          left: (offset / total * 100).toFixed(3) + '%',
          width: (duracion / total * 100).toFixed(3) + '%',
          widthNum: duracion / total * 100,
          color: COLORES[idx % COLORES.length],
          esReceso: false,
        });
        if (idx !== this.modulosConFechasCalculadas.length - 1 && mod.receso_dias > 0) {
          const rStart = this.addDiasStr(mod.fecha_fin, 1);
          const rEnd = this.addDiasStr(mod.fecha_fin, mod.receso_dias);
          const rStartD = new Date(rStart + 'T00:00:00');
          const rEndD = new Date(rEnd + 'T00:00:00');
          const rOffset = Math.round((rStartD - base) / 86400000);
          const rDuracion = Math.round((rEndD - rStartD) / 86400000) + 1;
          eventos.push({
            key: 'receso-' + mod.numero,
            nombre: `Receso (${mod.receso_dias}d)`,
            inicio: rStart,
            fin: rEnd,
            left: (rOffset / total * 100).toFixed(3) + '%',
            width: (rDuracion / total * 100).toFixed(3) + '%',
            widthNum: rDuracion / total * 100,
            color: '#bdbdbd',
            esReceso: true,
          });
        }
      });
      return eventos;
    },

    todayOffset() {
      if (!this.programaFechaInicio || !this.programaFechaFin) return null;
      const today = new Date().toISOString().slice(0, 10);
      if (today < this.programaFechaInicio || today > this.programaFechaFin) return null;
      const base = new Date(this.programaFechaInicio + 'T00:00:00');
      const offset = Math.round((new Date(today + 'T00:00:00') - base) / 86400000);
      return (offset / this.totalDiasPrograma * 100).toFixed(3) + '%';
    },

    totalSemanasPrograma() {
      return this.modulos.reduce((sum, m) => sum + (m.duracion_semanas || 0), 0);
    },

    totalDiasClasesPrograma() {
      return this.modulos.reduce(
        (sum, m) => sum + (m.duracion_semanas || 0) * (this.config.dias_clases_semana || 0),
        0
      );
    },

    totalDiasReceso() {
      // el último módulo no tiene receso
      return this.modulos
        .slice(0, -1)
        .reduce((sum, m) => sum + (m.receso_dias || 0), 0);
    },
  },

  methods: {
    toggleDia(valor) {
      const idx = this.config.dias_semana.indexOf(valor);
      if (idx === -1) {
        if (this.config.dias_semana.length >= this.config.dias_clases_semana) return;
        this.config.dias_semana.push(valor);
      } else {
        this.config.dias_semana.splice(idx, 1);
      }
    },

    clampModulo(mod, campo, min, max) {
      const val = Number(mod[campo]);
      mod[campo] = isNaN(val) ? min : Math.max(min, Math.min(max, val));
    },

    aplicarDuracionATodos() {
      const val = Math.max(1, Math.min(52, this.aplicarATodos.duracion_semanas || 1));
      this.modulos.forEach((m) => { m.duracion_semanas = val; });
    },

    aplicarRecesoATodos() {
      const val = Math.max(0, Math.min(60, this.aplicarATodos.receso_dias || 0));
      // aplica a todos excepto el último
      this.modulos.forEach((m, idx) => {
        if (idx < this.modulos.length - 1) m.receso_dias = val;
      });
    },

    calcularDiasModulo(mod) {
      const semanas = mod.duracion_semanas || 0;
      const diasPorSemana = this.config.dias_clases_semana || 0;
      const total = semanas * diasPorSemana;
      return total > 0 ? `${total} d.` : '—';
    },

    async cargarPrograma() {
      if (!this.programaId) return;
      this.loading = true;
      try {
        const { data } = await xaxios.get(
          `${process.env.VUE_APP_API_REST}/esam/programa/id/${this.programaId}`
        );
        this.programaInfo = data;
        this.programa = { nombre_compuesto: data.nombre_compuesto };
        this.estado_programa_codigo = data.estado_programa_codigo
          || data.estado_programa?.codigo
          || null;

        // Configuración global del programa
        if (data.dias_clases_semana) {
          this.config.dias_clases_semana = data.dias_clases_semana;
        }
        if (Array.isArray(data.dias_semana) && data.dias_semana.length) {
          this.config.dias_semana = data.dias_semana;
        }

        // Módulos con valores reales del backend
        const modulosOrdenados = [...(data.programa_modulo || [])].sort(
          (a, b) => a.numero - b.numero
        );
        this.modulos = modulosOrdenados.map((pm) => ({
          programa_modulo_id: pm.programa_modulo_id,
          numero: pm.numero,
          nombre: pm.nombre || `Módulo ${pm.numero}`,
          duracion_semanas: pm.duracion_semanas || 4,
          receso_dias: pm.receso_dias ?? 0,
        }));

        this.configOriginal = this.snapshotActual();
        await this.getBrochure();
        this.cargarFechasProyectadas();
        this.cargarEstadoConfiguracion();
      } catch (err) {
        window.console.error('Error cargando programa:', err);
        this.notificar('Error al cargar el programa', 'error');
      } finally {
        this.loading = false;
      }
    },

    async cargarConfiguracion() {
      const url = `${process.env.VUE_APP_API_REST}/api/v1/programa/${this.programaId}/configuracion-clases`;
      try {
        const res = await xaxios.get(url);
        const d = res.data;
        this.config.dias_clases_semana = d.dias_clases_semana ?? DEFAULT_CONFIG().dias_clases_semana;
        this.config.dias_semana = d.dias_semana ?? DEFAULT_CONFIG().dias_semana;
        if (Array.isArray(d.configuracion_modulos)) {
          d.configuracion_modulos.forEach((cm) => {
            const mod = this.modulos.find((m) => m.numero === cm.numero);
            if (mod) {
              mod.duracion_semanas = cm.duracion_semanas ?? mod.duracion_semanas;
              mod.receso_dias = cm.receso_dias ?? mod.receso_dias;
            }
          });
        }
        this.configOriginal = this.snapshotActual();
      } catch {
        this.configOriginal = this.snapshotActual();
      }
    },

    snapshotActual() {
      return JSON.stringify({
        config: { ...this.config, dias_semana: [...this.config.dias_semana] },
        modulos: this.modulos.map((m) => ({
          numero: m.numero,
          duracion_semanas: m.duracion_semanas,
          receso_dias: m.receso_dias,
        })),
      });
    },

    resetForm() {
      if (!this.configOriginal) return;
      const snap = JSON.parse(this.configOriginal);
      this.config = { ...snap.config };
      snap.modulos.forEach((sm) => {
        const mod = this.modulos.find((m) => m.numero === sm.numero);
        if (mod) {
          mod.duracion_semanas = sm.duracion_semanas;
          mod.receso_dias = sm.receso_dias;
        }
      });
    },

    async guardar() {
      if (this.config.dias_semana.length < this.config.dias_clases_semana) {
        const faltan = this.config.dias_clases_semana - this.config.dias_semana.length;
        this.notificar(`Faltan ${faltan} día(s) de la semana por seleccionar`, 'warning');
        return;
      }
      this.saving = true;
      const body = {
        dias_clases_semana: this.config.dias_clases_semana,
        dias_semana: this.config.dias_semana,
        fecha_inicio_estimada: this.programaFechaInicio || null,
        fecha_fin_estimada: this.programaFechaFin || null,
        configuracion_modulos: this.modulos.map((m, idx) => {
          const fechas = this.modulosConFechasCalculadas.find((f) => f.numero === m.numero) || {};
          return {
            programa_modulo_id: m.programa_modulo_id,
            numero: m.numero,
            duracion_semanas: m.duracion_semanas,
            receso_dias: idx < this.modulos.length - 1 ? m.receso_dias : 0,
            fecha_inicio_estimada: fechas.fecha_inicio || null,
            fecha_fin_estimada: fechas.fecha_fin || null,
          };
        }),
      };
      const url = `${process.env.VUE_APP_API_REST}/api/v1/programa/${this.programaId}/configuracion-clases`;
      try {
        await xaxios.put(url, body);
        this.configOriginal = this.snapshotActual();
        this.notificar('Configuración guardada correctamente', 'success');
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) {
          this.notificar('Endpoint aún no disponible en el servidor', 'warning');
        } else if (status === 400) {
          const msg = err?.response?.data?.error || '';
          if (msg.includes('no cumple los umbrales para anticipar')) {
            this.errorAnticipoMsg = msg;
            this.dialogErrorAnticipo = true;
          } else {
            this.notificar(msg || 'Error al guardar la configuración', 'error');
          }
        } else if (status === 403) {
          this.tieneHistorialConfig = true;
          this.notificar('Ya existe una configuración previa. Usa "Solicitar Cambio" en vez de guardar directamente.', 'warning');
        } else {
          this.notificar('Error al guardar la configuración', 'error');
        }
        window.console.error('Error guardando configuración:', err);
      } finally {
        this.saving = false;
      }
    },

    async cargarEstadoConfiguracion() {
      this.loadingHistorialConfig = true;
      const base = `${process.env.VUE_APP_API_REST}/api/v1/programa/${this.programaId}`;
      const [histConfig, histSol] = await Promise.allSettled([
        xaxios.get(`${base}/configuracion-clases/historial`),
        xaxios.get(`${base}/configuracion-clases/solicitudes`),
      ]);
      if (histConfig.status === 'fulfilled') {
        const arr = histConfig.value.data?.data ?? histConfig.value.data ?? [];
        this.tieneHistorialConfig = Array.isArray(arr) ? arr.length > 0 : !!arr;
      }
      // Fallback: si el historial está vacío o falló pero el programa ya tiene
      // configuración guardada (dias_clases_semana viene del servidor ≠ null),
      // se considera que ya existe historial previo.
      if (!this.tieneHistorialConfig && this.programaInfo?.dias_clases_semana) {
        this.tieneHistorialConfig = true;
      }
      if (histSol.status === 'fulfilled') {
        const list = histSol.value.data?.data?.historial ?? [];
        this.historialSolicitudes = Array.isArray(list) ? list : [];
        this.solicitudPendiente = this.historialSolicitudes.find((s) => s.estado === 'SOLICITADO') || null;
      }
      this.loadingHistorialConfig = false;
    },

    async solicitarCambio() {
      if (!this.descripcionSolicitud) {
        this.notificar('Ingresa el motivo del cambio solicitado', 'warning');
        return;
      }
      if (this.config.dias_semana.length < this.config.dias_clases_semana) {
        const faltan = this.config.dias_clases_semana - this.config.dias_semana.length;
        this.notificar(`Faltan ${faltan} día(s) de la semana por seleccionar`, 'warning');
        return;
      }
      this.savingSolicitud = true;
      const payload = {
        dias_clases_semana: this.config.dias_clases_semana,
        dias_semana: this.config.dias_semana,
        fecha_inicio_estimada: this.programaFechaInicio || null,
        fecha_fin_estimada: this.programaFechaFin || null,
        configuracion_modulos: this.modulos.map((m, idx) => {
          const fechas = this.modulosConFechasCalculadas.find((f) => f.numero === m.numero) || {};
          return {
            programa_modulo_id: m.programa_modulo_id,
            numero: m.numero,
            duracion_semanas: m.duracion_semanas,
            receso_dias: idx < this.modulos.length - 1 ? m.receso_dias : 0,
            fecha_inicio_estimada: fechas.fecha_inicio || null,
            fecha_fin_estimada: fechas.fecha_fin || null,
          };
        }),
      };
      try {
        await xaxios.post(
          `${process.env.VUE_APP_API_REST}/api/v1/programa/${this.programaId}/configuracion-clases/solicitudes`,
          { payload_solicitado: payload, descripcion: this.descripcionSolicitud }
        );
        this.descripcionSolicitud = '';
        this.notificar('Solicitud enviada — queda pendiente de aprobación', 'success');
        await this.cargarEstadoConfiguracion();
      } catch (err) {
        const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al enviar la solicitud';
        this.notificar(msg, 'error');
      } finally {
        this.savingSolicitud = false;
      }
    },

    async aprobarSolicitud(sol) {
      this.loadingRespuesta = sol.id;
      try {
        await xaxios.patch(
          `${process.env.VUE_APP_API_REST}/api/v1/configuracion-clases/solicitudes/${sol.id}`,
          { estado: 'APROBADO' }
        );
        this.notificar('Solicitud aprobada y configuración actualizada', 'success');
        await this.cargarEstadoConfiguracion();
        await this.cargarPrograma();
      } catch (err) {
        const msg = err?.response?.data?.error || 'Error al aprobar la solicitud';
        this.notificar(msg, 'error');
      } finally {
        this.loadingRespuesta = null;
      }
    },

    abrirRechazar(sol) {
      this.solicitudAgestionar = sol;
      this.formRechazar.respuesta = '';
      this.dialogRechazar = true;
    },

    async confirmarRechazar() {
      if (!this.formRechazar.respuesta) {
        this.notificar('El motivo de rechazo es requerido', 'warning');
        return;
      }
      this.loadingRespuesta = this.solicitudAgestionar.id;
      try {
        await xaxios.patch(
          `${process.env.VUE_APP_API_REST}/api/v1/configuracion-clases/solicitudes/${this.solicitudAgestionar.id}`,
          { estado: 'RECHAZADO', respuesta: this.formRechazar.respuesta }
        );
        this.dialogRechazar = false;
        this.notificar('Solicitud rechazada', 'info');
        await this.cargarEstadoConfiguracion();
      } catch (err) {
        const msg = err?.response?.data?.error || 'Error al rechazar la solicitud';
        this.notificar(msg, 'error');
      } finally {
        this.loadingRespuesta = null;
      }
    },

    async enviarDecision() {
      this.loadingDecision = true;
      try {
        await xaxios.post(
          `${process.env.VUE_APP_API_REST}/api/v1/programa/${this.programaId}/decision-arrastre-critico`,
          { decision: this.decisionTipo, descripcion: this.decisionDescripcion || undefined }
        );
        this.dialogDecision = false;
        this.decisionTipo = null;
        this.decisionDescripcion = '';
        this.notificar('Decisión registrada correctamente', 'success');
        await this.cargarPrograma();
      } catch (err) {
        const msg = err?.response?.data?.error || 'Error al registrar la decisión';
        this.notificar(msg, 'error');
      } finally {
        this.loadingDecision = false;
      }
    },

    brochureSelect() {
      this.brochuresel = !this.archivoBrochure;
      this.subidoresBrochure = false;
      this.msgBrochure = '';
    },

    async getBrochure() {
      try {
        const { data } = await xaxios.get(
          `${process.env.VUE_APP_API_REST}/api/v1/esam/programa/${this.programaId}/brochure`
        );
        this.brochureArchivoId = data?.data?.archivo_id || null;
      } catch {
        this.brochureArchivoId = null;
      }
    },

    async subirBrochure() {
      if (!this.archivoBrochure || !this.programaId) return;
      this.cargandoBrochure = true;
      this.msgBrochure = '';
      this.subidoresBrochure = false;
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(this.archivoBrochure);
        });
        const { data } = await xaxios.post(
          `${process.env.VUE_APP_API_REST}/api/v1/esam/programa/${this.programaId}/brochure`,
          { base64 }
        );
        this.brochureArchivoId = data?.data?.id || null;
        this.subidoresBrochure = true;
        this.archivoBrochure = null;
        this.brochuresel = true;
      } catch (e) {
        this.msgBrochure =
          e?.response?.data?.message || e?.response?.data?.error || 'Error al subir el brochure';
      } finally {
        this.cargandoBrochure = false;
      }
    },

    async verBrochure() {
      if (!this.programaId) return;
      this.dialogBrochure = true;
      this.brochureDataUri = null;
      this.cargandoVisualizacion = true;
      try {
        const { data } = await xaxios.get(
          `${process.env.VUE_APP_API_REST}/api/v1/esam/programa/${this.programaId}/brochure?include_file=1`
        );
        const archivo = data?.data?.archivo;
        if (!archivo || !archivo.file64) {
          this.notificar('Este programa no tiene brochure disponible', 'warning');
          this.dialogBrochure = false;
          return;
        }
        const bytes = atob(archivo.file64);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const blob = new Blob([arr], { type: archivo.mimetype || 'application/pdf' });
        this.brochureDataUri = URL.createObjectURL(blob);
      } catch {
        this.notificar('Error al obtener el brochure', 'error');
        this.dialogBrochure = false;
      } finally {
        this.cargandoVisualizacion = false;
      }
    },

    descargarBrochure() {
      if (!this.brochureDataUri) return;
      const nombre = this.programa?.nombre_compuesto || `brochure-${this.programaId}`;
      const a = document.createElement('a');
      a.href = this.brochureDataUri;
      a.download = `${nombre}.pdf`;
      a.click();
    },

    cerrarDialogBrochure() {
      this.dialogBrochure = false;
      if (this.brochureDataUri) {
        URL.revokeObjectURL(this.brochureDataUri);
        this.brochureDataUri = null;
      }
    },

    async eliminarBrochure() {
      if (!this.programaId) return;
      const confirmado = await this.$root.$confirm
        .open('Eliminar Brochure', '¿Está seguro de eliminar el brochure de este programa?', { color: 'red' })
        .catch(() => false);
      if (!confirmado) return;
      this.eliminandoBrochure = true;
      try {
        await xaxios.delete(
          `${process.env.VUE_APP_API_REST}/api/v1/esam/programa/${this.programaId}/brochure`
        );
        this.brochureArchivoId = null;
        this.subidoresBrochure = false;
        this.notificar('Brochure eliminado correctamente', 'success');
      } catch {
        this.msgBrochure = 'Error al eliminar el brochure';
      } finally {
        this.eliminandoBrochure = false;
      }
    },

    async cargarFechasProyectadas() {
      this.loadingFechas = true;
      try {
        const { data } = await xaxios.get(
          `${process.env.VUE_APP_API_REST}/api/v1/programa/${this.programaId}/fechas-proyectadas`
        );
        this.fechasProyectadas = data.fechas_proyectadas || [];
      } catch {
        this.fechasProyectadas = [];
      } finally {
        this.loadingFechas = false;
      }
    },

    traducirTipoFecha(tipo) {
      const map = {
        FIN_CAMPANIA: 'Fin de campaña regular',
        INICIO_ARRASTRE: 'Inicio de arrastre',
        FIN_ARRASTRE: 'Fin de arrastre',
        INICIO_ARRASTRE_CRITICO: 'Inicio de arrastre crítico',
        FIN_ARRASTRE_CRITICO: 'Fin de arrastre crítico',
        INICIO_MODULO_0: 'Inicio posible de Módulo 0',
      };
      return map[tipo] || tipo;
    },

    esPasado(fecha) {
      return fecha && new Date(fecha) < new Date();
    },

    colorEstadoDestino(codigo) {
      const colores = {
        PROXIMO: 'grey', CAMPANIA: 'green darken-1', ARRASTRE: 'yellow darken-3',
        ARRASTRE_CRITICO: 'orange darken-2', INICIADO_MODULO_0: 'blue darken-2',
        DESARROLLO: 'blue darken-3', CULMINADO: 'purple', DADO_DE_BAJA: 'red darken-2',
      };
      return colores[codigo] || 'grey';
    },

    formatFecha(iso) {
      if (!iso) return '—';
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    },

    leyendaColor(idx) {
      const VUETIFY_HEX = [
        '#1565c0', '#283593', '#6a1b9a',
        '#00695c', '#2e7d32', '#00838f',
        '#4527a0', '#37474f',
      ];
      return VUETIFY_HEX[idx % VUETIFY_HEX.length];
    },

    fechaModulo(numero) {
      return this.modulosConFechasCalculadas.find((m) => m.numero === numero) || {};
    },

    addDiasStr(iso, n) {
      if (!iso) return null;
      return dateToStr(addDays(new Date(iso + 'T00:00:00'), n));
    },

    modalidadLabel(m) {
      const map = {
        SEMIPRESENCIAL: 'Semipresencial', PRESENCIAL: 'Presencial', VIRTUAL: 'Virtual',
        S: 'Semipresencial', P: 'Presencial', V: 'Virtual',
      };
      return map[m] || m || '—';
    },

    modalidadIcon(m) {
      const map = { SEMIPRESENCIAL: 'mdi-lan-connect', PRESENCIAL: 'mdi-account-group', VIRTUAL: 'mdi-laptop', S: 'mdi-lan-connect', P: 'mdi-account-group', V: 'mdi-laptop' };
      return map[m] || 'mdi-help-circle-outline';
    },

    estadoLabel(e) {
      return parseFasePrograma(e) || '—';
    },

    estadoColor(e) {
      const colores = { 0: 'red darken-2', 1: 'blue darken-2', 2: 'green darken-2', 3: 'orange darken-2', 4: 'grey darken-1' };
      return colores[e] !== undefined ? colores[e] : 'grey';
    },

    parseModalidadDisplay(m) {
      return parseModalidadInverse(m) || this.modalidadLabel(m);
    },

    notificar(msg, color = 'success') {
      this.snackbar = { show: true, msg, color };
    },
  },

  mounted() {
    this.cargarPrograma();
  },
};
</script>

<style scoped>
.info-box {
  transition: box-shadow 0.15s ease;
}
.info-box:hover {
  box-shadow: 0 1px 4px rgba(0,0,0,0.12);
}
</style>
